import { Box, Button, Heading, Text } from '@zillow/constellation-10';
import { logError } from '@zg-rentals/log-error';

import { DISPLAY_CONTEXT } from '../PaymentGateway/constants';

import { PREMIUM_FEATURES_BY_CONTEXT } from '../../constants/FreemiumConstants';

import LearnAboutPremiumButton, { RENDER_AS_TYPE } from '../LearnAboutPremiumButton';
import PremiumProductOptionsSimple from '../PremiumProductOptionsSimple';

import { StyledBgFullWidth, StyledContentContainer } from '../StyledComponents/StyledLayout';

type PremiumFeatureSummaryProps = {
  componentContext?: string;
  displayContentFullWidth?: boolean;
  hideCompareAndLearnMore?: boolean;
  offsetPageWizardTopMargin?: boolean;
  onPublishClick?: () => void;
  reducePaddingTop?: boolean;
  premiumCompareRef?: React.RefObject<HTMLDivElement>;
};

const PremiumFeatureSummary = ({
  componentContext = DISPLAY_CONTEXT.MODULAR_LISTING,
  displayContentFullWidth = false,
  hideCompareAndLearnMore = false,
  onPublishClick,
  offsetPageWizardTopMargin = true,
  reducePaddingTop = false,
  premiumCompareRef,
}: PremiumFeatureSummaryProps) => {
  const gridColumnEndLeftColumn = displayContentFullWidth
    ? { base: 'span 6', lg: 'span 12' }
    : { base: 'span 6', lg: 'span 12', xxl: 'span 7' };

  const gridColumnEndRightColumn = displayContentFullWidth
    ? { base: 'span 6', lg: 'span 12' }
    : { base: 'span 6', lg: 'span 12', xxl: 'span 5' };

  const executeScroll = () => {
    if (premiumCompareRef?.current) {
      const element = premiumCompareRef.current;

      try {
        // StyledContentContainer in PageWizard
        const targetElement = element.parentElement?.parentElement?.parentElement?.parentElement?.parentElement;

        if (!targetElement) {
          throw new Error('Could not find parent element for scroll.');
        }

        const offsetPosition = element.offsetTop - 100;
        targetElement.scrollTo({ top: offsetPosition, behavior: 'smooth' });
      } catch (error) {
        logError({ error, context: 'PremiumFeatureSummary.executeScroll' });
        console.error('ERROR:', error);
      }
    }
  };

  const handleCompareOptionsClick = () => {
    executeScroll();
  };

  const renderPremiumUpsellItemList = () => {
    return Object.entries(PREMIUM_FEATURES_BY_CONTEXT).map(([key, value]) => {
      const { HEADING, BODY, ICON: Icon } = value.PAY_AND_PUBLISH;

      return (
        <Box key={key} css={{ display: 'flex', flexDirection: 'row', marginBottom: '600' }}>
          {/* need div container to prevent mismatching icon sizes */}
          <div>
            <Icon size={{ default: 'md' }} marginRight={{ default: 'sm' }} />
          </div>

          <Box>
            <Text textStyle="body-bold" asChild>
              <p>{HEADING}</p>
            </Text>
            <Text asChild>
              <p>{BODY}</p>
            </Text>
          </Box>
        </Box>
      );
    });
  };

  return (
    <Box
      data-testid="PremiumFeatureSummary"
      css={{
        position: 'relative',
        marginTop: offsetPageWizardTopMargin ? '-32px' : undefined,
      }}
    >
      <StyledBgFullWidth>
        <StyledContentContainer>
          <Box css={{ paddingTop: reducePaddingTop ? '400' : '800' }}>
            <Box
              css={{
                display: 'grid',
                gridTemplateColumns: {
                  base: 'repeat(6, 1fr)',
                  // 769
                  lg: 'repeat(12, 1fr)',
                },
                gap: '400',
              }}
            >
              <Box css={{ gridColumnEnd: gridColumnEndLeftColumn }}>
                <Heading level={1} textStyle="heading-md" fontFamily="heading-alt" css={{ marginBottom: '600' }}>
                  Your listing is active! Get the most with premium.
                </Heading>

                <Box data-testid="PremiumUpsellDetailsContainer" css={{ marginBottom: '600' }}>
                  {renderPremiumUpsellItemList()}
                </Box>

                {!hideCompareAndLearnMore && (
                  <Box css={{ display: 'flex', flexDirection: 'row', marginBottom: '1200' }}>
                    <Button
                      tone="brand"
                      emphasis="outlined"
                      css={{ marginRight: '800' }}
                      onClick={handleCompareOptionsClick}
                    >
                      Compare your options
                    </Button>
                    <LearnAboutPremiumButton as={RENDER_AS_TYPE.TEXT_BUTTON}>Learn more</LearnAboutPremiumButton>
                  </Box>
                )}
              </Box>

              <Box css={{ gridColumnEnd: gridColumnEndRightColumn, justifyContent: 'center', marginBottom: '600' }}>
                <PremiumProductOptionsSimple componentContext={componentContext} onPublishClick={onPublishClick} />
              </Box>
            </Box>
          </Box>
        </StyledContentContainer>
      </StyledBgFullWidth>
    </Box>
  );
};

export default PremiumFeatureSummary;
